import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faMinusCircle } from '@fortawesome/free-solid-svg-icons';
import DateBox from 'devextreme-react/date-box';
import { DrawApi, FileApi } from '@apply/api';
import { DrawType } from '@apply/constants';
import { PayeeFormModal } from '../modals/PayeeFormModal';
import { FinalizeDrawModal } from '../modals/FinalizeDrawModal';
import { useEffect, useState } from 'react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

import { SubmitButton } from '../buttons/SubmitButton';
import { CancelButton } from '../buttons/CancelButton';
import { isMobileScreen, hasPermission } from '@apply/ui';
import { PermissionType } from '@apply/constants';
import { ConfirmModal } from '@apply/ui';

import { useRecoilState } from 'recoil';
import { toggleArrayState } from '../recoil_drawState';

interface IHeaderPanelDrawColumnProps {
    draw: DrawApi.IDraw;
    isAdmin: boolean;
    currentApproval?: DrawApi.IDrawBudgetApproval;
    isEquityMet: boolean;
    isInspectionRequireFirst: boolean;
    applicationInsights: ApplicationInsights;
    borrowerName: string;
    latestDrawFundedTotal: number;
    latestDrawBorrowerRequestTotal: number;
    onDrawBorrowerRequestedChanged: (id: number, date: Date) => void;
    onDrawFundedOnChanged: (id: number, date: Date) => void;
    onDrawInspectionOrderedChanged: (id: number, date: Date) => void;
    onDrawInspectedOnChanged: (id: number, date: Date) => void;
    finalizeSnackbar: (value: boolean) => void;
    onDrawNotesChanged: (id: number, notes: string) => void;
    onDrawFinalized: (id: number) => void;
    onDrawBorrowerSubmitted: (id: number) => void;
    showFeedback: (value: boolean) => void;
    onDrawDeleted: (id: number) => void;
    onDrawOOBAccomodationWaiverChanged: (id: number, isWaived: boolean) => void;
}

export const HeaderPanelDrawColumn = ({
    draw,
    isAdmin,
    currentApproval,
    isEquityMet,
    isInspectionRequireFirst,
    applicationInsights,
    borrowerName,
    latestDrawFundedTotal,
    latestDrawBorrowerRequestTotal,
    onDrawBorrowerRequestedChanged,
    onDrawFundedOnChanged,
    onDrawInspectionOrderedChanged,
    onDrawInspectedOnChanged,
    finalizeSnackbar,
    onDrawNotesChanged,
    onDrawFinalized,
    onDrawBorrowerSubmitted,
    showFeedback,
    onDrawDeleted,
    onDrawOOBAccomodationWaiverChanged,
}: IHeaderPanelDrawColumnProps): JSX.Element => {
    const fileApi = new FileApi.FileApi(applicationInsights);

    const hasPermissionToWaive = useMemo(() => hasPermission(PermissionType.DrawWaiveBalance), []);
    const isWaiveEnabled = useMemo(
        () => isAdmin && draw.isLatestDraw === true && hasPermissionToWaive,
        [isAdmin, draw.isLatestDraw, hasPermissionToWaive]
    );

    const [isPayeeModalOpen, set_isPayeeModalOpen] = useState<boolean>(false);
    const [isFinalizeModalOpen, set_isFinalizeModalOpen] = useState<boolean>(false);

    const [inspectionOrderedBackgroundColor, set_inspectionOrderedBackgroundColor] = useState<string>('');
    const [inpsectionOnBackgroundColor, set_inpsectionOnBackgroundColor] = useState<string>('');
    const [fundedOnBackgroundColor, set_fundedOnBackgroundColor] = useState<string>('');
    const [openConfirmDelete, set_openConfirmDelete] = useState(false);
    const [deleteDraw, set_deleteDraw] = useState(false);

    const [toggleArray, rset_toggleArray] = useRecoilState(toggleArrayState);


    useEffect(() => {
        if (!draw?.isFinalized && draw?.borrowerRequested && draw?.inspectionOrdered && draw?.inspectedOn && draw?.fundedOn) {
            finalizeSnackbar(true);
        }
    }, [draw?.isFinalized, draw?.borrowerRequested, draw?.inspectionOrdered, draw?.inspectedOn, draw?.fundedOn]);

    useEffect(() => {
        if (draw.fundedOn && draw.borrowerRequested > draw.fundedOn) {
            set_fundedOnBackgroundColor('input-background-light-yellow');
        } else {
            set_fundedOnBackgroundColor('');
        }
    }, [draw.borrowerRequested, draw.fundedOn]);

    useEffect(() => {
        if (draw.inspectedOn && draw.borrowerRequested > draw.inspectedOn) {
            set_inpsectionOnBackgroundColor('input-background-light-yellow');
        } else {
            set_inpsectionOnBackgroundColor('');
        }
    }, [draw.borrowerRequested, draw.inspectedOn]);

    useEffect(() => {
        if (draw.inspectionOrdered && draw.borrowerRequested > draw.inspectionOrdered) {
            set_inspectionOrderedBackgroundColor('input-background-light-yellow');
        } else {
            set_inspectionOrderedBackgroundColor('');
        }
    }, [draw.borrowerRequested, draw.inspectionOrdered]);

    const headerWidth = (): number => {
        // Checking window width to apply mobile style
        const mobileScreen = isMobileScreen();
        if (mobileScreen) return 0;

        if (toggleArray[draw.drawNumber] !== true) return 100;

        switch (draw.drawType) {
            case DrawType.Automatic:
                return 600;
            case DrawType.Manual:
                return 700;
            default:
                throw new Error('HeaderPanelDrawColumn - could not determine draw type.');
        }
    };

    const onIconClick = () => {
        const newArray = toggleArray.map((_value, index) => {
            if (index === draw.drawNumber) {
                return !toggleArray[index];
            }

            return false;
        });
        rset_toggleArray(newArray);
    };

    const onPrintPayeeFormButtonClick = () => {
        set_isPayeeModalOpen(true);
    };

    const confirmDeleteMessage = (drawNumber: number) => {
        if (deleteDraw) {
            return `Are you sure you wish to delete this draw? (Draw #${drawNumber + 1})`;
        }
    };

    const handleConfirmDelete = async (drawId: number) => {
        onDrawDeleted(drawId);
        set_openConfirmDelete(false);
    };

    const handleCancelDelete = () => {
        set_openConfirmDelete(false);
    };

    const onDeleteDrawButtonClick = () => {
        set_deleteDraw(true);
        set_openConfirmDelete(true);
    };

    const CancelDrawButton = () => {
        if (!draw.isFinalized && !draw.inspectionOrdered) {
            return (
                <button
                    onClick={() => onDeleteDrawButtonClick()}
                    className='button header-panel-draw-column-button'
                >
                    Delete
                </button>
            );
        } else return <></>;
    };

    const onFinalizeDrawButtonClick = () => {
        set_isFinalizeModalOpen(true);
    };

    const FinalizeButton = () => {
        if (!draw.borrowerRequested || !draw.inspectionOrdered || !draw.inspectedOn || !draw.fundedOn) {
            return (
                <button
                    id='finalizeDrawButton'
                    disabled={!!draw.borrowerRequested && !!draw.inspectionOrdered && !!draw.inspectedOn && !!draw.fundedOn}
                    title='Dates must be completed before finalizing.'
                    className='button primary readOnly header-panel-draw-column-button'
                >
                    Finalize
                </button>
            );
        } else if (latestDrawFundedTotal <= 0) {
            return (
                <button
                    id='finalizeDrawButton'
                    disabled={!!draw.borrowerRequested && !!draw.inspectionOrdered && !!draw.inspectedOn && !!draw.fundedOn}
                    title='Funded amount must be greater than zero.'
                    className='button primary readOnly header-panel-draw-column-button'
                >
                    Finalize
                </button>
            );
        } else if (draw.isFinalized) {
            return (
                <button
                    id='finalizeDrawButton'
                    className='button primary header-panel-draw-column-button'
                    title='This draw has already been finalized.'
                    disabled={true}
                >
                    Finalized
                </button>
            );
        } else {
            return (
                <button
                    id='finalizeDrawButton'
                    onClick={onFinalizeDrawButtonClick}
                    className='button primary header-panel-draw-column-button'
                >
                    Finalize
                </button>
            );
        }
    };

    const downloadfile = async (token: string) => {
        const response = await fileApi.downloadUrl({ token });

        if (response.statusCode !== 200) return;
        window.location.href = response.result.downloadUrl;
    };

    const WaivedCheckBox = () => {
        let title = '';
        if (!isWaiveEnabled) {
            if (!draw.isLatestDraw) title = 'Only current draws can be waived.';
            if (!hasPermissionToWaive) title = 'You do not have permission to waive an out of balance draw.';
            if (!isAdmin) title = 'You must be an admin to waive an out of balance draw.';
        }

        return (
            <div
                className='draw-header-waived-container'
                title={title}
            >
                <input
                    type='checkbox'
                    checked={draw.isOutOfBalanceWaived}
                    disabled={!isWaiveEnabled}
                    className='draw-head-center-checkbox-input'
                    onChange={() => onDrawOOBAccomodationWaiverClicked()}
                />
                <label
                    htmlFor='equityRequirement'
                    className='draw-head-center-checkbox-label'
                    onChange={() => onDrawOOBAccomodationWaiverClicked()}
                >
                    Waived
                </label>
            </div>
        );
    };

    const SignatureDownloadButton = () => {
        if (draw?.drawSignatureDownloadToken) {
            return (
                <button
                    onClick={() => downloadfile(draw?.drawSignatureDownloadToken)}
                    className='button primary header-panel-draw-column-button'
                >
                    Signature
                </button>
            );
        }

        return (
            <button
                disabled={true}
                title='No signature file was found.'
                className='button primary readOnly header-panel-draw-column-button'
            >
                Signature
            </button>
        );
    };

    const closePayeeModal = () => {
        set_isPayeeModalOpen(false);
    };

    const closeFinalizeModal = () => {
        set_isFinalizeModalOpen(false);
    };

    const submittedFinalizeModal = (id: number) => {
        set_isFinalizeModalOpen(false);
        onDrawFinalized(id);
    };

    const onDrawOOBAccomodationWaiverClicked = () => {
        if (isWaiveEnabled) {
            const oobAccomodationConfirmMessage =
                'Are you sure you want to change the ' +
                (draw.drawType === DrawType.Automatic ? 'Accomodation' : 'Out of Balance') +
                ' status?';
            if (window.confirm(oobAccomodationConfirmMessage) === true) {
                onDrawOOBAccomodationWaiverChanged(draw.id, draw.isOutOfBalanceWaived);
            }
        }
    };

    if (toggleArray[draw.drawNumber] === true) {
        return (
            <div
                className='draw-header-panel'
                style={{ width: headerWidth() }}
            >
                <div className='draw-header-panel-center'>
                    <div className='draw-icon'>
                        <FontAwesomeIcon
                            icon={faMinusCircle}
                            size={'2x'}
                            onClick={onIconClick}
                        />
                    </div>
                    <div className='draw-header-control-panel'>
                        <div className='draw-header-draw-column-date-container'>
                            <div className='draw-header-input-center-panel'>
                                <label htmlFor='borrowerRequested'>Borrower Requested:</label>
                                <DateBox
                                    value={draw.borrowerRequested}
                                    onValueChange={(date) => onDrawBorrowerRequestedChanged(draw.id, date)}
                                    className='draw-date-box'
                                />
                            </div>
                            <div className='draw-header-input-center-panel'>
                                <label htmlFor='inspectionOrdered'>Inspection Ordered:</label>
                                <DateBox
                                    value={draw.inspectionOrdered}
                                    className={`draw-date-box ${inspectionOrderedBackgroundColor}`}
                                    disabled={!!draw.inspectionId}
                                    onValueChange={(date) => onDrawInspectionOrderedChanged(draw.id, date)}
                                />
                            </div>
                            <div className='draw-header-input-center-panel'>
                                <label htmlFor='inspectedOn'>Inspection On:</label>
                                <DateBox
                                    value={draw.inspectedOn}
                                    className={`draw-date-box ${inpsectionOnBackgroundColor}`}
                                    disabled={!draw.inspectionOrdered}
                                    onValueChange={(date) => onDrawInspectedOnChanged(draw.id, date)}
                                />
                            </div>
                            <div className='draw-header-input-center-panel'>
                                <label htmlFor='fundedOn'>Funded On:</label>
                                <DateBox
                                    value={draw?.fundedOn}
                                    className={`draw-date-box ${fundedOnBackgroundColor}`}
                                    onValueChange={(date) => onDrawFundedOnChanged(draw.id, date)}
                                />
                            </div>
                        </div>
                        <div className='header-panel-draw-column'>
                            <WaivedCheckBox />
                            <SignatureDownloadButton />
                            <button
                                onClick={onPrintPayeeFormButtonClick}
                                className='button primary header-panel-draw-column-button'
                            >
                                Payee Form
                            </button>
                            <FinalizeButton />
                            <CancelDrawButton />
                            <ConfirmModal
                                isOpen={openConfirmDelete}
                                text={confirmDeleteMessage(draw.drawNumber)}
                                confirmCallback={() => handleConfirmDelete(draw.id)}
                                cancelCallback={() => handleCancelDelete()}
                            />
                        </div>
                    </div>
                    <PayeeFormModal
                        applicationInsights={applicationInsights}
                        closeCallback={closePayeeModal}
                        draw={draw}
                        isOpen={isPayeeModalOpen}
                    />
                    <FinalizeDrawModal
                        applicationInsights={applicationInsights}
                        closeCallback={closeFinalizeModal}
                        submittedFinalizedDrawCallback={submittedFinalizeModal}
                        draw={draw}
                        isOpen={isFinalizeModalOpen}
                    />
                </div>
                <div className='header-panel-draw-note-container'>
                    <p>Notes:</p>
                    <input
                        type='text'
                        className=''
                        value={draw.notes ?? ''}
                        onChange={(e) => onDrawNotesChanged(draw.id, e.target.value)}
                    />
                </div>
            </div>
        );
    } else {
        if (isAdmin) {
            return (
                // Only Admins can expand Draw Columns for extra functionality
                <div
                    className='draw-header-panel'
                    style={{ width: headerWidth() }}
                >
                    <div className='draw-icon'>
                        <FontAwesomeIcon
                            icon={faPlusCircle}
                            size={'2x'}
                            onClick={onIconClick}
                        />
                    </div>
                </div>
            );
        } else {
            return (
                <div
                    className='draw-header-panel draw-manual-submit-button-div'
                    style={{ width: headerWidth() }}
                >
                    <SubmitButton
                        draw={draw}
                        currentApproval={currentApproval}
                        isEquityMet={isEquityMet}
                        applicationInsights={applicationInsights}
                        borrowerName={borrowerName}
                        latestDrawBorrowerRequestTotal={latestDrawBorrowerRequestTotal}
                        onDrawBorrowerSubmitted={onDrawBorrowerSubmitted}
                        showFeedback={showFeedback}
                    />
                    {!isInspectionRequireFirst && !draw.needsBorrowerSignature && (
                        <CancelButton
                            draw={draw}
                            onDrawDeleted={onDrawDeleted}
                        />
                    )}
                </div>
            );
        }
    }
};
